// LoginCountWebSocketInitializer.tsx
import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { FeedActionContext, FeedStateContext } from './FeedState';
import { useStateSelector, AuthenticationInfoSelector } from '../global/dataSelectors';
import { useDispatcher } from '../global/hooks';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { useQuotesFeedStatus, useQuotesWebSocketCloseReason, useFeedStatus, useSpreadsFeedStatus } from './FeedSelectors';
import { Websockets } from '../../constants';
import _ from 'lodash';
import { quotesWebSocketService } from './QuotesWebSocketService';
import { webSocketService } from './WebSocketService';
import { useSubscribedSymbolsContext } from './context/DataFeedSymbolsContext';
import { useFeedActions } from './useFeedActions';
import { NotificationActions } from '../global/actions';
import { useFeatureFlag } from '@tradingblock/components';
import { getQuoteSubscription as getOldQuoteSubscription } from './DataFeed';
import { DataFeedSubscription } from '@tradingblock/types';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const QuotesWebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const [isUsingFallback, setIsUsingFallback] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const hasUnsubscribedFromOld = useRef<boolean>(false);
  const oldWebSocketDataFlowCheck = useRef<NodeJS.Timeout>();
  const oldWebSocketLastDataTime = useRef<number>(0);
  const dispatchers = useDispatcher();
  const quotesFeedDispatcher = dispatchers.dispatcher.quotesFeed;
  const fullDispatcher = dispatchers.dispatcher;
  const globalDispatch = dispatchers.dispatch;
  const quotesFeedStatus = useQuotesFeedStatus();
  const oldFeedStatus = useFeedStatus();
  const closeReason = useQuotesWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);
  const apiToken = useStateSelector(state => state.auth.apiToken);
  const { subscribedQuotes } = useSubscribedSymbolsContext();
  const { subscribeQuote, unsubscribeQuote } = useFeedActions(apiToken);
  const { accountId } = useStateSelector(AuthenticationInfoSelector);
  // Helper to directly subscribe to old websocket (bypasses strategy selection for fallback)
  const subscribeToOldWebSocket = useCallback((subscription: DataFeedSubscription) => {
    if (!apiToken || !accountId) {
      return false;
    }
    // Check if old websocket is open before subscribing
    if (!webSocketService.isConnected()) {
      return false;
    }
    try {
      const cmd = getOldQuoteSubscription(apiToken, accountId, [subscription], false);
      if (cmd) {
        webSocketService.sendMessage(cmd);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Quotes: Error subscribing to old websocket', error);
      return false;
    }
  }, [apiToken, accountId]);

  // Helper to directly unsubscribe from old websocket (bypasses strategy selection)
  const unsubscribeFromOldWebSocket = useCallback((subscription: DataFeedSubscription) => {
    if (!apiToken || !accountId) {
      return false;
    }
    // Check if old websocket is open before unsubscribing
    if (!webSocketService.isConnected()) {
      return false;
    }
    try {
      const cmd = getOldQuoteSubscription(apiToken, accountId, [subscription], true);
      if (cmd) {
        webSocketService.sendMessage(cmd);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Quotes: Error unsubscribing from old websocket', error);
      return false;
    }
  }, [apiToken, accountId]);
  const { isEnabled: useNewQuoteService } = useFeatureFlag('new_quote_service');
  const { isEnabled: useNewSpreadService } = useFeatureFlag('new_spread_service');
  const spreadsFeedStatus = useSpreadsFeedStatus();

  // Monitor old websocket data flow when using fallback
  const startOldWebSocketDataFlowMonitoring = useCallback(() => {
    if (oldWebSocketDataFlowCheck.current) {
      clearInterval(oldWebSocketDataFlowCheck.current);
    }
    oldWebSocketLastDataTime.current = Date.now();
    const DATA_FLOW_TIMEOUT_MS = 30000; // 30 seconds

    oldWebSocketDataFlowCheck.current = setInterval(() => {
      if (!isUsingFallback || !subscribedQuotes.length) {
        if (oldWebSocketDataFlowCheck.current) {
          clearInterval(oldWebSocketDataFlowCheck.current);
          oldWebSocketDataFlowCheck.current = undefined;
        }
        return;
      }

      const timeSinceLastData = Date.now() - oldWebSocketLastDataTime.current;
      if (timeSinceLastData > DATA_FLOW_TIMEOUT_MS && oldFeedStatus === 'open') {
        // Both websockets are not receiving data - show notification
        globalDispatch(
          NotificationActions.add({
            status: 'Error',
            title: 'Quotes WebSocket Error',
            message: 'No data received from quotes websockets. Both primary and fallback are unresponsive.',
            global: false,
            time: new Date(),
            key: 'quotes-feed-error',
            read: false,
            hide: false,
            viewed: false,
          })
        );
        // Clear the interval to avoid repeated notifications
        if (oldWebSocketDataFlowCheck.current) {
          clearInterval(oldWebSocketDataFlowCheck.current);
          oldWebSocketDataFlowCheck.current = undefined;
        }
      }
    }, 5000);
  }, [isUsingFallback, subscribedQuotes, oldFeedStatus, globalDispatch]);

  // Stop monitoring old websocket data flow
  const stopOldWebSocketDataFlowMonitoring = useCallback(() => {
    if (oldWebSocketDataFlowCheck.current) {
      clearInterval(oldWebSocketDataFlowCheck.current);
      oldWebSocketDataFlowCheck.current = undefined;
    }
  }, []);

  // Handle fallback to old websocket when primary fails
  const handleFallbackToOldWebSocket = useCallback(() => {
    if (!isUsingFallback && useNewQuoteService) {
      setIsUsingFallback(true);
      hasUnsubscribedFromOld.current = false; // Reset flag when using fallback

      // Always ensure old websocket is connected (connect if not already open)
      if (oldFeedStatus !== 'open') {
        if (webSocketService.shouldTryReconnect() && canConnect) {
          webSocketService.connect(dispatch, dispatchers.dispatcher.feed, globalDispatch, fullDispatcher);
        }
      }

      // Start monitoring old websocket data flow
      // Note: Subscriptions are handled dynamically when symbols are added/removed
      // The subscription routing will automatically use old websocket when primary is unavailable
      if (oldFeedStatus === 'open' && subscribedQuotes.length > 0) {
        startOldWebSocketDataFlowMonitoring();
      }
    }
  }, [isUsingFallback, useNewQuoteService, oldFeedStatus, canConnect, dispatch, dispatchers.dispatcher.feed, globalDispatch, fullDispatcher, subscribedQuotes, startOldWebSocketDataFlowMonitoring]);

  // Set up data flow stopped callback (always set it up - service will check subscriptions before monitoring)
  useEffect(() => {
    quotesWebSocketService.setDataFlowStoppedCallback(handleFallbackToOldWebSocket);
    return () => {
      quotesWebSocketService.setDataFlowStoppedCallback(null);
    };
  }, [handleFallbackToOldWebSocket]);

  // Resubscribe when NEW websocket reconnects (backend maintains subscriptions per websocket instance)
  // Note: We don't resubscribe when old websocket reconnects - only when new websocket comes back
  const prevQuotesFeedStatus = useRef<typeof quotesFeedStatus>();

  useEffect(() => {
    // Only resubscribe when NEW websocket transitions from non-open to 'open'
    // This happens when primary websocket reconnects after being down
    if (
      useNewQuoteService &&
      quotesFeedStatus === 'open' &&
      prevQuotesFeedStatus.current !== 'open' &&
      subscribedQuotes.length > 0 &&
      accountId
    ) {
      const subscription = { kind: 'Quote' as const, symbols: subscribedQuotes };
      subscribeQuote(accountId, subscription);
    }
    prevQuotesFeedStatus.current = quotesFeedStatus;
  }, [quotesFeedStatus, subscribedQuotes, accountId, subscribeQuote, useNewQuoteService]);

  // Update monitoring based on subscription state
  useEffect(() => {
    const hasSubs = subscribedQuotes.length > 0;
    quotesWebSocketService.setHasSubscriptions(hasSubs);
  }, [subscribedQuotes]);

  useEffect(() => {
    if (shouldReconnect && apiToken) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (quotesWebSocketService.shouldTryReconnect()) {
          quotesWebSocketService
            .connect(dispatch, quotesFeedDispatcher, fullDispatcher, globalDispatch, apiToken)
            .then(() => {
              setShouldReconnect(false);
              setIsUsingFallback(false); // Reset fallback flag when primary reconnects
            });
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, apiToken]);

  useEffect(() => {
    if (quotesFeedStatus === 'closed') {
      // Check if old websocket is available as fallback (either open or can be connected)
      const oldWebSocketAvailable = oldFeedStatus === 'open' || (canConnect && webSocketService.shouldTryReconnect());

      if (canConnect) {
        // Always try to use old websocket as fallback when new service is enabled
        if (useNewQuoteService) {
          handleFallbackToOldWebSocket();
          // Keep trying to reconnect to primary in background
          const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
          retryTimeoutMs.current = newTimeout;
          setShouldReconnect(true);
          // Don't show notification if fallback is available or being attempted
          return;
        }

        // Only show notification if both websockets are down
        if (!oldWebSocketAvailable) {
          const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
          const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
          const closeMessage = `Quotes Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
          quotesFeedDispatcher.closed({
            reason: closeReason || '',
            retryingInSeconds: newTimeoutDisplay,
            message: closeMessage,
          });
          retryTimeoutMs.current = newTimeout;
          console.warn(closeMessage);
          globalDispatch(
            NotificationActions.add({
              status: 'Error',
              title: 'Quotes WebSocket Error',
              message: closeReason ? closeReason : 'Quotes Websocket error',
              global: false,
              time: new Date(),
              key: 'quotes-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        }
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Quotes Socket is closed because user can no longer connect';
        quotesFeedDispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
        // Show notification only if both are down
        if (!oldWebSocketAvailable) {
          globalDispatch(
            NotificationActions.add({
              status: 'Error',
              title: 'Quotes WebSocket Error',
              message: 'Quotes Websocket error',
              global: false,
              time: new Date(),
              key: 'quotes-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        }
      }
    } else if (quotesFeedStatus === 'open' && isUsingFallback) {
      // Primary reconnected, switch back
      setIsUsingFallback(false);
      stopOldWebSocketDataFlowMonitoring(); // Stop monitoring old websocket
      // Unsubscribe from old websocket if spreads service is also working (or not using new service)
      // Only unsubscribe if both new services are enabled and working, or if spreads doesn't use new service
      const shouldUnsubscribeFromOld = !useNewSpreadService || spreadsFeedStatus === 'open';
      if (shouldUnsubscribeFromOld && subscribedQuotes.length > 0 && accountId && !hasUnsubscribedFromOld.current) {
        const subscription = { kind: 'Quote' as const, symbols: subscribedQuotes };
        unsubscribeFromOldWebSocket(subscription);
        hasUnsubscribedFromOld.current = true;
      }
      // Note: New subscriptions will automatically route to primary websocket via useFeedActions
    }
  }, [quotesFeedStatus, closeReason, canConnect, oldFeedStatus, useNewQuoteService, useNewSpreadService, spreadsFeedStatus, isUsingFallback, handleFallbackToOldWebSocket, quotesFeedDispatcher, globalDispatch, subscribedQuotes, accountId, unsubscribeFromOldWebSocket, stopOldWebSocketDataFlowMonitoring]);

  // Subscribe to old websocket when it opens and we're using fallback
  useEffect(() => {
    if (isUsingFallback && oldFeedStatus === 'open' && subscribedQuotes.length > 0 && accountId && !hasUnsubscribedFromOld.current) {
      const subscription = { kind: 'Quote' as const, symbols: subscribedQuotes };
      subscribeToOldWebSocket(subscription);
      startOldWebSocketDataFlowMonitoring();
    } else {
      stopOldWebSocketDataFlowMonitoring();
    }
  }, [isUsingFallback, oldFeedStatus, subscribedQuotes, accountId, subscribeToOldWebSocket, startOldWebSocketDataFlowMonitoring, stopOldWebSocketDataFlowMonitoring]);

  // Monitor old websocket data flow when using fallback - track when quotes are received
  const feedState = useContext(FeedStateContext);

  useEffect(() => {
    if (isUsingFallback && oldFeedStatus === 'open' && subscribedQuotes.length > 0 && feedState) {
      // Check if we're receiving quotes for our subscribed symbols
      const hasReceivedQuotes = subscribedQuotes.some((symbol: string) => feedState.quotes[symbol]);
      if (hasReceivedQuotes || feedState.lastMessage) {
        // Reset the timer when we receive data
        oldWebSocketLastDataTime.current = Date.now();
      }
    }
  }, [isUsingFallback, oldFeedStatus, subscribedQuotes, feedState]);

  // Cleanup monitoring on unmount
  useEffect(() => {
    return () => {
      stopOldWebSocketDataFlowMonitoring();
    };
  }, [stopOldWebSocketDataFlowMonitoring]);

  // Unsubscribe from old websocket when both services are working
  useEffect(() => {
    // Only unsubscribe if:
    // 1. Quotes primary is working
    // 2. We're not using fallback
    // 3. Either spreads doesn't use new service OR spreads primary is also working
    // 4. We have subscriptions
    // 5. We haven't already unsubscribed
    if (
      useNewQuoteService &&
      quotesFeedStatus === 'open' &&
      !isUsingFallback &&
      (!useNewSpreadService || spreadsFeedStatus === 'open') &&
      subscribedQuotes.length > 0 &&
      accountId &&
      !hasUnsubscribedFromOld.current
    ) {
      const subscription = { kind: 'Quote' as const, symbols: subscribedQuotes };
      unsubscribeFromOldWebSocket(subscription);
      hasUnsubscribedFromOld.current = true;
    } else if (isUsingFallback) {
      // Reset flag when using fallback
      hasUnsubscribedFromOld.current = false;
    }
  }, [quotesFeedStatus, spreadsFeedStatus, isUsingFallback, useNewQuoteService, useNewSpreadService, subscribedQuotes, accountId, unsubscribeQuote]);

  useEffect(() => {
    if (canConnect && apiToken) {
      if (useNewQuoteService) {
        quotesWebSocketService.connect(dispatch, quotesFeedDispatcher, fullDispatcher, globalDispatch, apiToken);
      }
    } else if (!canConnect && !quotesWebSocketService.shouldTryReconnect()) {
      quotesWebSocketService.closeConnection();
    }
  }, [quotesFeedDispatcher, fullDispatcher, globalDispatch, dispatch, canConnect, apiToken, useNewQuoteService]);

  return null; // This component does not render anything
};

export default QuotesWebSocketInitializer;
